// filepath: c:\Users\USER\Desktop\GitHub Repos\github-emu-portal\src\components\MainNavBar\MainNavBar.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { DescriptionOutlined, LoginOutlined, SupportAgentOutlined } from "@mui/icons-material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EngineeringIcon from "@mui/icons-material/Engineering";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";

import { isEcrOpsRole, isOperationsUserInAnyOrg } from "components/RequirePermission/RequireEcrOpsRole";
import { isLeadershipInAnyOrg } from "components/RequirePermission/RequireLeadershipRole";
import { useAccessPermissions, useCurrentUserRoleNames } from "components/RequirePermission/useAccessPermissions";
import { useGetUserAccessQuery } from "state/rtkQuery/access/accessQueries";

import "./sideNavbar.css";

const SideNavBar = () => {
    const [collapsed, setCollapsed] = useState(true);
    const sidebarRef = React.useRef(null);

    // Get user's first name from Redux
    const firstName = useSelector(state => state.auth?.userAccount?.givenName);
    const email = useSelector(state => state.auth?.userAccount?.username);

    // ECR Ops Portal is restricted to OperationsAdmin/EnterpriseAdmin
    // (global roles) OR OperationsUser in at least one org (see
    // RequireEcrOpsRole, the route guard this link must stay consistent
    // with) — hidden here too so no one sees a link they'd immediately be
    // blocked from. OperationsUser is a per-org membership value, not a
    // global role (same shape as Leadership below), so this checks GET
    // /user/organizations for OperationsUser in ANY org, same source and
    // same rule as the route guard itself.
    // Leadership shares this same nav slot but never enters /ecr-ops at all
    // (see RequireLeadershipRole) — for them it points straight at their one
    // page, Enterprise Usage, instead. Leadership is also a per-org
    // membership value (not a global role — see RequireLeadershipRole's own
    // comment), checked the same way.
    useAccessPermissions();
    const roleNames = useCurrentUserRoleNames();
    const { data: orgAccessData } = useGetUserAccessQuery({ email }, { skip: !email });
    const canSeeEcrOps = isEcrOpsRole(roleNames) || isOperationsUserInAnyOrg(orgAccessData?.organization);
    const canSeeLeadershipEnterpriseUsage = isLeadershipInAnyOrg(orgAccessData?.organization);

    // Handle scroll position for fixed sidebar
    useEffect(() => {
        const handleScroll = () => {
            if (sidebarRef.current) {
                if (window.scrollY > 64) {
                    sidebarRef.current.classList.add('fixed-sidebar');
                } else {
                    sidebarRef.current.classList.remove('fixed-sidebar');
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className={`main-sidebar-wrapper ${collapsed ? 'collapsed' : ''}`} >
            <aside className="main-sidebar" ref={sidebarRef}>
                <div className="sidebar-header">
                    <EngineeringIcon className="sidebar-logo" />
                    {!collapsed && <span className="sidebar-title"> {firstName ? `Welcome ${firstName}!` : ""}</span>}
                    <button
                        className="collapse-btn"
                        onClick={() => setCollapsed(!collapsed)}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </button>
                </div>

                <div className="sidebar-divider"></div>

                <nav className="sidebar-nav">
                    <NavLink
                        to="/dashboard"
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        title="Home Dashboard"
                    >
                        <HomeOutlinedIcon className="nav-icon" />
                        {!collapsed && <span className="nav-text">Home Dashboard</span>}
                    </NavLink>

                    <NavLink
                        to="/global-orgs"
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        title="Organizations"
                    >
                        <ViewListOutlinedIcon className="nav-icon" />
                        {!collapsed && <span className="nav-text">Organizations</span>}
                    </NavLink>

                    {(canSeeEcrOps || canSeeLeadershipEnterpriseUsage) && (
                        <NavLink
                            to={canSeeEcrOps ? "/ecr-ops" : "/leadership/copilot-usage"}
                            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                            title={canSeeEcrOps ? "ECR Ops Portal" : "Copilot Usage Dashboard"}
                        >
                            <AutoAwesomeIcon className="nav-icon" />
                            {!collapsed && (
                                <span className="nav-text">{canSeeEcrOps ? "ECR Ops Portal" : "Copilot Usage Dashboard"}</span>
                            )}
                        </NavLink>
                    )}
                    <NavLink
                        to="https://deloitteglobal.service-now.com/sp?id=sc_cat_item&sys_id=4a417b4f1b17855cbcae55b0604bcb1c"
                        className="nav-item"
                        title="Support Ticket"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <SupportAgentOutlined className="nav-icon" />
                        {!collapsed && <span className="nav-text">Support Ticket</span>}
                    </NavLink>

                    <NavLink
                        to="https://github.com/enterprises/deloitte-global/sso"
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        title="GitHub Sign-In"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LoginOutlined className="nav-icon" />
                        {!collapsed && <span className="nav-text">GitHub Sign-In</span>}
                    </NavLink>

                    <NavLink
                        to="https://developer.deloitte.com/enterprisecoderepository/"
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        title="ECR Documentation"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <DescriptionOutlined className="nav-icon" />
                        {!collapsed && <span className="nav-text">ECR Documentation</span>}
                    </NavLink>
                </nav>
            </aside>
        </div>
    );
};

export default SideNavBar;